import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import { useAppInsightsContext, useTrackEvent } from '@microsoft/applicationinsights-react-js';
import Box from '@mui/material/Box';
import { sha256 } from 'js-sha256';
import React, { Profiler, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { notificationInterval } from 'src/layout/AppLayout/constants';
import { fetchCarouselNotifications, carouselDismiss, } from '../../apis/notifications.api';
import { languages, locale } from '../../config/language.config';
import { useGlobalStateContext } from '../../context/GlobalStateContext/GlobalStateContext';
import { useIsDesktop } from '../../utils/mediaQueries';
import { Carousel } from '../Carousel/Carousel';
import NotificationCard from '../NotificationCard/NotificationCard';
import { carouselContainer } from './styles';
const CAROUSEL_MSG_LIMIT = 3;
export const NotificationCarousel = () => {
    const isDesktop = useIsDesktop();
    const { i18n: { language }, } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [notificationList, setNotificationList] = useState([]);
    const [activeIndex, setActiveIndex] = useState(0);
    const [carouselCards, setCarouselCards] = useState([]);
    const { globalState: { merchant, loadedScrollSections, setLoadedScrollSections }, } = useGlobalStateContext();
    const notif_language = language?.includes(languages.fr) ? locale.fr : locale.en;
    const getCarouselNotifications = async () => {
        try {
            const response = await fetchCarouselNotifications(merchant.merchantNumber, CAROUSEL_MSG_LIMIT, notif_language);
            // Filter data where the notification is not already in the list
            const deduplicatedData = response.data.filter((notification) => !notificationList.find((notificationListItem) => notification.id == notificationListItem.id));
            // Merge and limit to 3 notifs
            setNotificationList((prevList) => [...prevList, ...deduplicatedData].slice(0, CAROUSEL_MSG_LIMIT));
            setLoading(false);
        }
        catch (error) {
            console.error(error);
        }
    };
    // Inittial call
    useEffect(() => {
        if (!merchant?.merchantNumber)
            return;
        getCarouselNotifications();
    }, [merchant?.merchantNumber]);
    // Initialize the fetch interval
    const getCarouselFetchInterval = useCallback(() => setInterval(getCarouselNotifications, notificationInterval), [notificationList]);
    useEffect(() => {
        // Reset interval on notifcation list change
        const intervalId = getCarouselFetchInterval();
        setCarouselCards(notificationList?.map((notification) => renderCards(notification)));
        return () => clearInterval(intervalId);
    }, [notificationList]);
    function onCloseCard(notificationUuid) {
        const updatedList = notificationList ? [...notificationList] : [];
        const index = updatedList.findIndex((notification) => notification.notificationUuid === notificationUuid);
        index !== -1 && updatedList.splice(index, 1);
        setNotificationList(updatedList);
        setActiveIndex(0);
        carouselDismiss(merchant.merchantNumber, notificationUuid);
    }
    const renderCards = (notification) => {
        const { id, content, datetimeSend, notificationCardType, notificationUuid } = notification || {};
        if (!content)
            return _jsx(_Fragment, {});
        return (_jsx(NotificationCard, { content: content, dateSent: datetimeSend, type: notificationCardType, showCrossIcon: true, isCarousel: notificationList.length > 1, readStatus: true, onCloseCard: () => onCloseCard(notificationUuid), sectionName: 'Notification Carousel' }, id));
    };
    const appInsights = useAppInsightsContext();
    const trackResponseTime = useTrackEvent(appInsights, 'Response Time', {});
    const timerStartTime = useRef(0);
    const timerOn = useRef(false);
    const onRender = (id, phase, actualDuration, baseDuration, startTime, commitTime) => {
        if (loading && !timerOn.current) {
            // If APIs are loading and the timer is off, turn on the timer and record the start time
            timerOn.current = true;
            timerStartTime.current = startTime;
        }
        else if (!loading && timerOn.current) {
            // If APIs finish loading and the timer is on, turn off the timer and calculate response time by subtracting start time from commit time
            const responseTime = (commitTime - timerStartTime.current) / 1000;
            const logData = {
                MerchantID: localStorage.getItem('idi-merchant-no') || '',
                HashedEmail: sha256(localStorage.getItem('idi-merchant-username')?.toLowerCase() || ''),
                Section: id,
                ResponseTime: responseTime,
                TimeGenerated: new Date().toISOString(),
            };
            // Send log to Azure App Insights
            trackResponseTime(logData);
            // Reset the timer
            timerStartTime.current = 0;
            timerOn.current = false;
        }
    };
    useEffect(() => {
        notificationList?.length > 0 &&
            setLoadedScrollSections({ ...loadedScrollSections, notifications: false });
    }, []);
    const setScrollSectionReady = () => {
        if (notificationList && !loading && loadedScrollSections?.notifications === false) {
            setLoadedScrollSections({ ...loadedScrollSections, notifications: true });
        }
    };
    return (_jsx(Profiler, { id: 'Notification Carousel', onRender: onRender, children: loading || notificationList.length === 0 ? (_jsx(_Fragment, {})) : notificationList.length > 1 ? (_jsx(Box, { "data-testid": 'carousel-notifications', ref: () => setScrollSectionReady(), sx: carouselContainer(isDesktop), children: _jsx(Carousel, { activeIndex: activeIndex, setActiveIndex: setActiveIndex, children: carouselCards }) })) : (_jsx(Box, { "data-testid": 'single-notification', ref: () => setScrollSectionReady(), sx: { padding: '1.5rem 2.5rem' }, children: carouselCards })) }));
};
